import { getProductPackagingTemplate } from "@/api/template";
import FakeLink from "@/components/FakeLink/FakeLink";
import FileDownloader from "@/components/FileDownloader/FileDownloader";
import FileUploader from "@/components/FileUploader/FileUploader";
import PageButtonGroup from "@/components/PageButtonGroup/PageButtonGroup";
import { COLLECTING_BREADCRUMB, PageHeaderNew } from "@/components/PageHeader/PageHeaderNew";
import StatusTag from "@/components/StatusTag/StatusTag";
import StatusTags from "@/components/StatusTags/StatusTags";
import SwitchWithLabel from "@/components/SwitchWithLabel/SwitchWithLabel";
import Table, { Column } from "@/components/Table/Table";
import useTableStateUrl from "@/components/Table/hooks/useTableStateUrl";
import TableHeader from "@/components/TableHeader/TableHeader";
import YearSelector from "@/components/YearSelector/YearSelector";
import useAnalytics from "@/hooks/useAnalytics";
import { useTableSelection } from "@/hooks/useTableSelection";
import useTrackOnLoad from "@/hooks/useTrackPageview";
import useUrlStateBoolean from "@/hooks/useUrlStateBoolean";
import DeletePackagingAction from "@/pages/packaging/DeletePackagingAction";
import { DeleteOutlined, DownloadOutlined } from "@ant-design/icons";
import { Button, Tag, Tooltip } from "antd";
import { useDashboardYear } from "../dashboard/hooks/useDateRange";
import styles from "./PackagingOverview.module.scss";
import { useGetPackagings, useGetPackagingsPerYear } from "./usePackagings";

export type PackagingStatus = "complete" | "incomplete";

export interface Packaging {
  id: number;
  external_id: string;
  name?: string;
  status: PackagingStatus;
  deletable: boolean;
  relations: number;
  bom_mass?: number;
}

export default function PackagingOverview() {
  useTrackOnLoad("Packaging Overview Viewed");

  const analytics = useAnalytics();
  const { year } = useDashboardYear();
  const [showUnrelated, setShowUnrelated] = useUrlStateBoolean("showUnrelated", false);

  const columns: Column<Packaging>[] = [
    {
      key: "external_id",
      label: "Packaging ID",
      searchable: true,
      sortable: true,
      minWidth: "190px",
    },
    {
      key: "name",
      label: "Packaging",
      maxWidth: "3fr",
      minWidth: "160px",
      sortable: true,
      searchable: true,
    },
    {
      key: "relations",
      label: "Relations",
      sortable: true,
      minWidth: "150px",
      renderer: (record) =>
        record.relations ? (
          <Tag>
            {record.relations} product{record.relations !== 1 ? "s" : ""}
          </Tag>
        ) : (
          "-"
        ),
    },
    {
      key: "status",
      label: "Status",
      minWidth: "150px",
      maxWidth: "150px",
      renderer: (packaging) => (
        <StatusTag
          showIcon={false}
          status={packaging.status}
        />
      ),
      filterable: true,
    },
  ];

  const { pagination, filter, search, sort, onSearchFilterSortPaginate, setPage } =
    useTableStateUrl({
      columns,
      defaultSort: { key: "external_id", order: "ascend" },
    });
  const { data, isLoading, isFetching } = useGetPackagings({
    year,
    showUnrelated,
    filter,
    sort,
    search,
    pagination,
  });

  const { selectedItems, selectedKeys, setSelectedItems, onSelectedKeysChange } =
    useTableSelection<Packaging>({
      data: data?.data,
    });

  const deselectUndeletablePackaging = () => {
    setSelectedItems((selectedItems) => [...selectedItems.filter((x) => x.deletable)]);
  };

  const { data: packagingYears } = useGetPackagingsPerYear();

  const resetTable = () => {
    setPage(1);
    setSelectedItems([]);
  };

  const packagingStatus = [
    { label: "Complete", value: "complete" },
    { label: "Incomplete", value: "incomplete" },
  ];

  return (
    <div className={styles.PackagingOverview}>
      <PageHeaderNew
        title="Product packaging"
        description="Overview of all product packaging IDs that require a bill of materials (BOM). Upload individually or in bulk."
        breadcrumbs={[COLLECTING_BREADCRUMB]}
        // breadcrumbsRight={
        //   <>
        //     <HelpButton
        //       size="small"
        //       type="link"
        //       url="#"
        //     />
        //   </>
        // }
        titleRight={
          <PageButtonGroup>
            <FileUploader
              uploadType={"packaging"}
              uploadDataTableProps={{
                columns: [
                  { key: "packaging_id", label: "Packaging ID" },
                  { key: "level", label: "Level" },
                  { key: "name", label: "Name" },
                  { key: "quantity", label: "Quantity" },
                  { key: "unit", label: "Unit" },
                  { key: "supplier_name", label: "Supplier name" },
                  { key: "supplier_address", label: "Supplier address" },
                ],
                rowKey: "id",
              }}
            />
            <FileDownloader
              fileOutputName={`template-product-packaging.xlsx`}
              fileUrlFn={getProductPackagingTemplate}
              buttonText="Template"
              icon={<DownloadOutlined />}
              onDownload={() => analytics.track("Packaging Template Downloaded")}
            />
          </PageButtonGroup>
        }
      />
      <TableHeader
        noRoundedTop
        left={
          <>
            {selectedKeys.length > 0 ? (
              <>
                <span className={styles["num-selected"]}>{selectedKeys.length} selected:</span>
                <PageButtonGroup>
                  <DeletePackagingAction
                    deselectUndeletableProducts={deselectUndeletablePackaging}
                    packagings={selectedItems}
                    onSucces={() => {
                      setSelectedItems([]);
                      analytics.track("Packaging Deleted");
                    }}
                  >
                    <Button disabled={selectedItems.some((x) => !x.deletable)}>
                      <DeleteOutlined style={{ color: "#F5222D" }} />
                    </Button>
                  </DeletePackagingAction>
                </PageButtonGroup>
                <FakeLink onClick={() => setSelectedItems([])}>Clear selection</FakeLink>
              </>
            ) : (
              <YearSelector
                disableYears={showUnrelated}
                optionData={packagingYears}
                onChange={resetTable}
                right={
                  <Tooltip
                    placement="bottom"
                    title="Show packaging that are not related to purchase or sales orders"
                  >
                    <>
                      <SwitchWithLabel
                        togglePosition="right"
                        size="small"
                        checked={showUnrelated}
                        onChange={(disabled) => {
                          resetTable();
                          setShowUnrelated(disabled);
                        }}
                      >
                        Year unrelated
                      </SwitchWithLabel>
                    </>
                  </Tooltip>
                }
              />
            )}
          </>
        }
        right={
          <StatusTags
            data={data}
            loading={isLoading}
          />
        }
      />
      <Table<Packaging>
        availableFilters={{
          status: packagingStatus,
        }}
        loading={isLoading || isFetching}
        rowKey="id"
        data={data?.data}
        onSearchFilterSortPaginate={onSearchFilterSortPaginate}
        totalRows={data?.total_count}
        pagination={pagination}
        sort={sort}
        search={search}
        filter={filter}
        columns={columns}
        rowLink={(record) => `/packaging/${record.id}/bom`}
        onSelectedKeysChange={onSelectedKeysChange}
        selectedKeys={selectedKeys}
        checkable={true}
      />
    </div>
  );
}
